import type { ComponentProps } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import type TextButton from 'LuxKit/Button/TextButton'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addToUserCart } from 'actions/UserCartActions'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import OfferCheckoutButton from './OfferCheckoutButton'
import { isFlightItem } from 'lib/checkout/checkoutUtils'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  packageView: App.OfferPackageOptionView
  addToCartAndKeepBrowsing?: boolean
  overrideRooms?: Array<App.RoomOccupants | App.Occupants>
  onSubmit?: () => void
  isCharterBundle?: boolean
}

function OfferSelectPackageButton(props: Props) {
  const {
    isCharterBundle = false,
    packageView,
    addToCartAndKeepBrowsing,
    onSubmit,
    overrideRooms,
    ...rest
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()

  const onProceedToBooking = useCallback((items: Array<App.Checkout.AnyItem>) => {
    const flights = items.some(isFlightItem)
    if (flights) {
      // Pick regular flow and final decision to pay or only add to cart to be made at end of flight selection
      dispatch(initialiseCheckout(items, { mode: 'vertical-select' }))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))

      if (isCharterBundle) {
        const itemsReadyToAdd = items.filter(item => (item.itemType === 'flight' && item.flights.length > 0) || (item.itemType !== 'flight'))
        dispatch(addToUserCart(itemsReadyToAdd, true, false))
      }
    } else {
      dispatch(addToUserCart(items))
    }
  }, [dispatch, isCharterBundle])

  return (
    <OfferCheckoutButton
      {...rest}
      kind="primary"
      onSubmit={onSubmit}
      overrideRooms={overrideRooms}
      packageView={packageView}
      onProceedToBooking={onProceedToBooking}
    >
      {intl.formatMessage({
        id: 'tSrD632SuGJudqzSKb8H',
        defaultMessage: 'Select package',
        description: 'Button text to select an offer package on the offer listing or detail page',
      })}
    </OfferCheckoutButton>
  )
}

export default OfferSelectPackageButton
